import type { FormEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import type { PageLink } from "../api/pages";
import { navigate, usePreviousPage } from "../lib/router";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { ToastContainer } from "../components/ui/toast";
import { SegmentedControl } from "../components/ui/segmented-control";
import { PageView, type PageViewData } from "../components/PageView";
import { PageBuilderHeader } from "./page-builder/PageBuilderHeader";
import {
  SlugField,
  type SlugCheckState,
} from "./page-builder/SlugField";
import { LinksList } from "./page-builder/LinksList";
import { SocialLinks } from "./page-builder/SocialLinks";
import {
  LinkModal,
  type LinkFormState,
  type LinkModalState,
} from "./page-builder/LinkModal";
import {
  DeletePageTrigger,
  DeletePageModal,
} from "./page-builder/DeletePageDeleteControls";
import { useLanguage } from "../hooks/useLanguage";

type TabType = "links" | "social";

type FormState = {
  slug: string;
  title: string;
  links_label: string;
  show_links_label: boolean;
  logo_base64: string;
  description: string;
  background_color: string;
  instagram_url: string;
  whatsapp_phone: string;
};

const INITIAL_STATE: FormState = {
  slug: "",
  title: "",
  links_label: "Meus links",
  show_links_label: true,
  logo_base64: "",
  description: "",
  background_color: "#000000",
  instagram_url: "",
  whatsapp_phone: "",
};

const DEFAULT_UNAVAILABLE_MESSAGE =
  "Esse endereço não está disponível. Escolha outro.";

function sanitizeSlugInput(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

function createEmptyLinkForm(): LinkFormState {
  return {
    label: "",
    url: "",
  };
}

type PageBuilderLayoutProps = {
  isEditing: boolean;
  form: FormState;
  slugCheck: SlugCheckState;
  hostLabel: string;
  links: PageLink[];
  socialLinks: import("../api/pages").PageSocialLink[];
  pageId: number | null;
  onSubmit: (e: FormEvent) => void;
  onSlugBlur: () => void;
  onChange: (field: keyof FormState) => (value: string) => void;
  onAddLink: () => void;
  onEditLink: (link: PageLink) => void;
  onDeleteLink: (link: PageLink) => void;
  saving: boolean;
  message: string | null;
};

function PageBuilderLayout({
  isEditing,
  form,
  slugCheck,
  hostLabel,
  links,
  pageId,
  onSubmit,
  onSlugBlur,
  onChange,
  onAddLink,
  onEditLink,
  onDeleteLink,
  saving,
  message,
}: PageBuilderLayoutProps) {
  const { t } = useLanguage();

  return (
    <Card className="p-8 border-neutral-200/60 shadow-xl shadow-neutral-900/5 bg-white/80 backdrop-blur-sm">
      <form className="space-y-8" onSubmit={onSubmit}>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-1">{t.pageBuilder.basicInfo.title}</h3>
            <p className="text-sm text-neutral-500">{t.pageBuilder.basicInfo.subtitle}</p>
          </div>

          <SlugField
            slug={form.slug}
            hostLabel={hostLabel}
            status={slugCheck}
            onChange={(value: string) => onChange("slug")(value)}
            onBlur={onSlugBlur}
          />

          <div className="space-y-2">
            <label
              htmlFor="title"
              className="text-sm font-medium text-neutral-700"
            >
              {t.pageBuilder.form.title.label}
            </label>
            <input
              id="title"
              value={form.title}
              onChange={e =>
                onChange("title")(e.target.value.slice(0, 120))
              }
              placeholder={t.pageBuilder.form.title.placeholder}
              className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:border-transparent placeholder:text-neutral-400"
            />

          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-neutral-700">
              {t.pageBuilder.form.logo.label}
            </label>
            <p className="text-xs text-neutral-500">{t.pageBuilder.form.logo.help}</p>

            {form.logo_base64 ? (
              <div className="flex items-center gap-4">
                <img
                  src={form.logo_base64}
                  alt="Logo preview"
                  className="w-20 h-20 rounded-full object-cover border-2 border-neutral-200"
                />
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        // Compress and convert to base64
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const img = new Image();
                          img.onload = () => {
                            const canvas = document.createElement('canvas');
                            let width = img.width;
                            let height = img.height;

                            // Resize to max 400x400 while maintaining aspect ratio
                            const maxSize = 400;
                            if (width > height && width > maxSize) {
                              height = (height / width) * maxSize;
                              width = maxSize;
                            } else if (height > maxSize) {
                              width = (width / height) * maxSize;
                              height = maxSize;
                            }

                            canvas.width = width;
                            canvas.height = height;
                            const ctx = canvas.getContext('2d');
                            ctx?.drawImage(img, 0, 0, width, height);

                            // Convert to JPEG with quality adjustment to reach ~500kb
                            let quality = 0.9;
                            let base64 = canvas.toDataURL('image/jpeg', quality);

                            // Reduce quality if still too large
                            while (base64.length > 500000 && quality > 0.1) {
                              quality -= 0.1;
                              base64 = canvas.toDataURL('image/jpeg', quality);
                            }

                            onChange('logo_base64')(base64);
                          };
                          img.src = event.target?.result as string;
                        };
                        reader.readAsDataURL(file);
                      }}
                    />
                    <span className="inline-flex items-center px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50 transition-colors">
                      {t.pageBuilder.form.logo.change}
                    </span>
                  </label>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => onChange('logo_base64')('')}
                  >
                    {t.pageBuilder.form.logo.remove}
                  </Button>
                </div>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;

                    // Compress and convert to base64
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      const img = new Image();
                      img.onload = () => {
                        const canvas = document.createElement('canvas');
                        let width = img.width;
                        let height = img.height;

                        // Resize to max 400x400 while maintaining aspect ratio
                        const maxSize = 400;
                        if (width > height && width > maxSize) {
                          height = (height / width) * maxSize;
                          width = maxSize;
                        } else if (height > maxSize) {
                          width = (width / height) * maxSize;
                          height = maxSize;
                        }

                        canvas.width = width;
                        canvas.height = height;
                        const ctx = canvas.getContext('2d');
                        ctx?.drawImage(img, 0, 0, width, height);

                        // Convert to JPEG with quality adjustment to reach ~500kb
                        let quality = 0.9;
                        let base64 = canvas.toDataURL('image/jpeg', quality);

                        // Reduce quality if still too large
                        while (base64.length > 500000 && quality > 0.1) {
                          quality -= 0.1;
                          base64 = canvas.toDataURL('image/jpeg', quality);
                        }

                        onChange('logo_base64')(base64);
                      };
                      img.src = event.target?.result as string;
                    };
                    reader.readAsDataURL(file);
                  }}
                />
                <div className="flex items-center justify-center w-full h-32 border-2 border-dashed border-neutral-300 rounded-xl hover:border-neutral-400 hover:bg-neutral-50 transition-colors">
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-neutral-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p className="mt-2 text-sm text-neutral-600">{t.pageBuilder.form.logo.upload}</p>
                    <p className="text-xs text-neutral-500">{t.pageBuilder.form.logo.formats}</p>
                  </div>
                </div>
              </label>
            )}
          </div>

          <div className="space-y-2">
            <label
              htmlFor="description"
              className="text-sm font-medium text-neutral-700"
            >
              {t.pageBuilder.form.description.label}
            </label>
            <textarea
              id="description"
              value={form.description}
              onChange={e =>
                onChange("description")(e.target.value.slice(0, 600))
              }
              placeholder={t.pageBuilder.form.description.placeholder}
              className="w-full min-h-[120px] rounded-xl border border-neutral-200 px-4 py-3 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:border-transparent placeholder:text-neutral-400 resize-y"
            />
            <p className="text-xs text-neutral-500 text-right">
              {form.description.length}/600
            </p>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="background_color"
              className="text-sm font-medium text-neutral-700"
            >
              {t.pageBuilder.form.appearance.label}
            </label>
            <div className="flex gap-3 items-center p-3 rounded-xl border border-neutral-200 bg-neutral-50/50">
              <div className="relative flex-shrink-0">
                <input
                  id="background_color"
                  type="color"
                  value={form.background_color}
                  onChange={e => onChange("background_color")(e.target.value)}
                  className="w-12 h-12 rounded-lg border-0 p-0 cursor-pointer overflow-hidden"
                />
                <div
                  className="absolute inset-0 rounded-lg ring-1 ring-inset ring-black/10 pointer-events-none"
                  style={{ backgroundColor: form.background_color }}
                />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-neutral-500 mb-1">{t.pageBuilder.form.appearance.colorLabel}</p>
                <input
                  type="text"
                  value={form.background_color}
                  onChange={e => onChange("background_color")(e.target.value)}
                  placeholder="#000000"
                  className="w-full bg-transparent border-none p-0 text-sm font-mono text-neutral-900 focus:ring-0 placeholder:text-neutral-400"
                />
              </div>
            </div>
          </div>
        </div>

        {message && (
          <div className="p-4 rounded-lg bg-red-50 text-red-600 text-sm text-center font-medium">
            {message}
          </div>
        )}
      </form>
    </Card>
  );
}

export function PageBuilder({ slug }: { slug?: string | null }) {
  const { t } = useLanguage();
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [pageId, setPageId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [slugCheck, setSlugCheck] = useState<SlugCheckState>({
    status: "idle",
  });
  const [links, setLinks] = useState<PageLink[]>([]);
  const [socialLinks, setSocialLinks] = useState<import("../api/pages").PageSocialLink[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>("links");
  const [initialData, setInitialData] = useState<{
    form: FormState;
    links: PageLink[];
    socialLinks: import("../api/pages").PageSocialLink[];
  } | null>(null);
  const [linkModalState, setLinkModalState] =
    useState<LinkModalState>({
      open: false,
      editingLink: null,
      form: createEmptyLinkForm(),
      saving: false,
      error: null,
    });
  const [deleteModalOpen, setDeleteModalOpen] =
    useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: "success" | "error" | "info" }>>([]);
  const currentSlugRef = useRef(form.slug);
  const previousPage = usePreviousPage();

  const addToast = useCallback((message: string, type: "success" | "error" | "info" = "info") => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  useEffect(() => {
    currentSlugRef.current = form.slug;
  }, [form.slug]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!initialData) return false;

    // Compare form data
    const formChanged = JSON.stringify(initialData.form) !== JSON.stringify(form);

    // Compare links (ignoring order and id for comparison)
    const linksChanged = initialData.links.length !== links.length ||
      !initialData.links.every(initialLink =>
        links.some(currentLink =>
          currentLink.label === initialLink.label &&
          currentLink.url === initialLink.url &&
          currentLink.type === initialLink.type
        )
      );

    // Compare social links
    const socialLinksChanged = initialData.socialLinks.length !== socialLinks.length ||
      !initialData.socialLinks.every(initialLink =>
        socialLinks.some(currentLink =>
          currentLink.network === initialLink.network &&
          currentLink.username === initialLink.username
        )
      );

    return formChanged || linksChanged || socialLinksChanged;
  }, [initialData, form, links, socialLinks]);

  // Warn user before leaving if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    let active = true;
    setMessage(null);
    setError(null);
    setNotFound(false);
    setLinks([]);

    if (!slug) {
      setForm(INITIAL_STATE);
      setPageId(null);
      setLoading(false);
      setSlugCheck({ status: "idle" });
      return;
    }

    setLoading(true);
    api.getMyPage(slug).then(res => {
      if (!active) return;
      if (!res.ok) {
        setError(res.error);
        if (res.error.toLowerCase().includes("autenticado")) {
          navigate("/login");
        }
      } else if (!res.data) {
        setNotFound(true);
      } else {
        const loadedForm = {
          slug: res.data.slug || "",
          title: res.data.title || "",
          links_label: res.data.links_label || "Meus links",
          show_links_label: !!res.data.show_links_label,
          logo_base64: res.data.logo_base64 || "",
          description: res.data.description || "",
          background_color: res.data.background_color || "#000000",
          instagram_url: "",
          whatsapp_phone: "",
        };
        const loadedLinks = res.data.links || [];
        const loadedSocialLinks = res.data.social_links || [];

        setForm(loadedForm);
        setLinks(loadedLinks);
        setSocialLinks(loadedSocialLinks);
        setInitialData({
          form: loadedForm,
          links: loadedLinks,
          socialLinks: loadedSocialLinks,
        });
        setPageId(res.data.id || null);
        if (res.data.slug) {
          setSlugCheck({ status: "available" });
        } else {
          setSlugCheck({ status: "idle" });
        }
      }
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, [slug]);

  const publicUrl = useMemo(() => {
    if (!form.slug) return null;
    if (typeof window === "undefined") return `/${form.slug}`;
    return `${window.location.origin}/${form.slug}`;
  }, [form.slug]);

  const hostLabel = useMemo(() => {
    if (typeof window === "undefined") return "seusite.com";
    const host = window.location.host;
    if (
      host === "0.0.0.0:3000" ||
      host === "127.0.0.1:3000"
    ) {
      return "seusite.com";
    }
    return host || "seusite.com";
  }, []);

  const onChange =
    (field: keyof FormState) =>
      (value: string) => {
        let nextValue = value;
        if (field === "slug") {
          nextValue = sanitizeSlugInput(value);
          setSlugCheck({ status: "idle" });
        }
        setForm(prev => ({ ...prev, [field]: nextValue }));
      };

  const canManageLinks = Boolean(pageId);

  const openLinkModal = (link?: PageLink) => {
    if (!pageId) {
      window.alert("Salve a página antes de adicionar links.");
      return;
    }
    setLinkModalState({
      open: true,
      editingLink: link || null,
      form: link
        ? {
          label: link.label || "",
          url: link.url || "",
        }
        : createEmptyLinkForm(),
      saving: false,
      error: null,
    });
  };

  const closeLinkModal = () => {
    setLinkModalState({
      open: false,
      editingLink: null,
      form: createEmptyLinkForm(),
      saving: false,
      error: null,
    });
  };

  const onLinkFormChange =
    (field: keyof LinkFormState) =>
      (value: string) => {
        setLinkModalState((prev: LinkModalState) => ({
          ...prev,
          form: {
            ...prev.form,
            [field]: value,
          },
        }));
      };

  const submitLinkForm = async (e: FormEvent) => {
    e.preventDefault();
    if (!pageId) {
      window.alert("Salve a página antes de adicionar links.");
      return;
    }

    setLinkModalState((prev: LinkModalState) => ({
      ...prev,
      saving: true,
      error: null,
    }));

    const { form: lf, editingLink } = linkModalState;

    const payload: any = {
      pageId,
      label: lf.label.trim() || undefined,
      url: lf.url.trim() || undefined,
    };

    const res = editingLink
      ? await api.updatePageLink({
        ...payload,
        id: editingLink.id,
      })
      : await api.createPageLink(payload);

    if (!res.ok) {
      setLinkModalState((prev: LinkModalState) => ({
        ...prev,
        saving: false,
        error: res.error,
      }));
      return;
    }

    if (editingLink) {
      setLinks(prev =>
        prev.map(link =>
          link.id === res.data.id
            ? res.data
            : link,
        ),
      );
    } else {
      setLinks(prev => [
        ...prev,
        res.data,
      ]);
    }

    setLinkModalState((prev: LinkModalState) => ({
      ...prev,
      saving: false,
    }));

    closeLinkModal();
  };

  const handleDeleteLink = async (link: PageLink) => {
    if (
      !window.confirm(
        "Tem certeza que deseja remover este link?",
      )
    )
      return;
    const res = await api.deletePageLink(
      link.id,
    );
    if (!res.ok) {
      window.alert(res.error);
      return;
    }
    setLinks(prev =>
      prev.filter(
        item => item.id !== link.id,
      ),
    );
  };

  const handleDeletePage = async () => {
    if (!pageId) return;
    setDeleting(true);
    const res = await api.deletePage({
      id: pageId,
    });
    setDeleting(false);
    if (!res.ok) {
      window.alert(
        res.error ||
        "Não foi possível excluir a página.",
      );
      return;
    }
    setDeleteModalOpen(false);
    navigate("/app");
  };

  const checkSlugAvailability = useCallback(
    async (
      slugValue: string,
    ): Promise<{
      available: boolean;
      message?: string;
      stale?: boolean;
    }> => {
      const targetSlug =
        slugValue.trim();
      if (!targetSlug) {
        setSlugCheck({
          status: "idle",
        });
        return { available: false };
      }

      setSlugCheck({
        status: "checking",
      });
      const res =
        await api.checkPageSlugAvailability(
          targetSlug,
          pageId ?? undefined,
        );

      if (
        currentSlugRef.current !==
        targetSlug
      ) {
        return {
          available: false,
          stale: true,
        };
      }

      if (!res.ok) {
        const fallback =
          res.error ||
          "Não foi possível verificar a disponibilidade do endereço.";
        setSlugCheck({
          status: "error",
          message: fallback,
        });
        return {
          available: false,
          message: fallback,
        };
      }

      if (res.data.available) {
        setSlugCheck({
          status: "available",
        });
        return { available: true };
      }

      const message =
        res.data.message ||
        DEFAULT_UNAVAILABLE_MESSAGE;
      setSlugCheck({
        status: "unavailable",
        message,
      });
      return { available: false, message };
    },
    [pageId],
  );

  const onSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    setMessage(null);
    if (!form.slug) {
      setMessage(
        "Escolha um endereço para sua página.",
      );
      return;
    }

    if (slugCheck.status === "checking") {
      window.alert(
        "Estamos verificando a disponibilidade do endereço. Aguarde um instante e tente novamente.",
      );
      return;
    }

    let availability =
      slugCheck.status === "available" &&
        currentSlugRef.current ===
        form.slug
        ? { available: true }
        : await checkSlugAvailability(
          form.slug,
        );

    if (availability.stale) {
      availability =
        await checkSlugAvailability(
          form.slug,
        );
      if (availability.stale) {
        window.alert(
          "Não foi possível verificar a disponibilidade do endereço. Tente novamente.",
        );
        return;
      }
    }

    if (!availability.available) {
      window.alert(
        availability.message ||
        DEFAULT_UNAVAILABLE_MESSAGE,
      );
      return;
    }

    setSaving(true);
    const payload = {
      id: pageId ?? undefined,
      slug: form.slug,
      title:
        form.title.trim() || undefined,
      links_label: form.links_label.trim() || undefined,
      show_links_label: form.show_links_label,
      logo_base64: form.logo_base64 || undefined,
      description:
        form.description.trim() ||
        undefined,
      background_color: form.background_color || undefined,
      instagram_url:
        form.instagram_url.trim() ||
        undefined,
      whatsapp_phone:
        form.whatsapp_phone || undefined,
    };
    const res = await api.savePage(
      payload,
    );
    setSaving(false);
    if (!res.ok) {
      setMessage(res.error);
      if (
        res.error
          .toLowerCase()
          .includes("autenticado")
      ) {
        navigate("/login");
      }
      return;
    }
    addToast("Página salva com sucesso!", "success");
    setPageId(res.data.id || null);
    const updatedForm = {
      slug: res.data.slug || "",
      title: res.data.title || "",
      links_label: res.data.links_label || "Meus links",
      show_links_label: !!res.data.show_links_label,
      logo_base64: res.data.logo_base64 || "",
      description:
        res.data.description || "",
      background_color: res.data.background_color || "#000000",
      instagram_url: "",
      whatsapp_phone: "",
    };
    const updatedLinks = res.data.links || [];
    const updatedSocialLinks = res.data.social_links || [];

    setForm(updatedForm);
    setLinks(updatedLinks);
    setSocialLinks(updatedSocialLinks);
    setInitialData({
      form: updatedForm,
      links: updatedLinks,
      socialLinks: updatedSocialLinks,
    });
    setSlugCheck({
      status: "available",
    });
    const targetPath =
      `/app/pagina/${res.data.slug}`;
    if (
      typeof window !==
      "undefined" &&
      location.pathname !==
      targetPath
    ) {
      navigate(targetPath);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white selection:bg-indigo-100 selection:text-indigo-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-neutral-900/10 via-white to-white -z-10" />
        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 -z-10 mix-blend-overlay"></div>
        <div className="text-center space-y-4">
          <div className="animate-spin mx-auto w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full"></div>
          <p className="text-slate-600 font-medium">Carregando informações da página...</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <p className="text-2xl font-semibold text-neutral-800">
          Página não encontrada
        </p>
        <p className="mt-2 text-neutral-500 max-w-sm">
          Não conseguimos localizar uma página com o
          endereço informado. Talvez ela tenha sido
          removida ou pertença a outro usuário.
        </p>
        <div className="mt-6 flex gap-3">
          <Button
            variant="primary"
            onClick={() => navigate("/app")}
          >
            Voltar para a lista
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/app/pagina")}
          >
            Criar nova página
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <p className="text-2xl font-semibold text-neutral-800">
          Algo deu errado
        </p>
        <p className="mt-2 text-neutral-500 max-w-sm">
          {error}
        </p>
        <Button
          variant="primary"
          className="mt-6"
          onClick={() => navigate("/login")}
        >
          Fazer login novamente
        </Button>
      </div>
    );
  }

  const isEditing = Boolean(slug);

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-8">
        <PageBuilderHeader
          isEditing={isEditing}
          onSave={() => onSubmit()}
          saving={saving}
          hasUnsavedChanges={hasUnsavedChanges}
        />

        <div className="grid gap-8 lg:grid-cols-[1fr_400px] xl:grid-cols-[1fr_440px] items-start">
          <div className="space-y-8 min-w-0">
            <PageBuilderLayout
              isEditing={isEditing}
              form={form}
              slugCheck={slugCheck}
              hostLabel={hostLabel}
              links={links}
              socialLinks={socialLinks}
              pageId={pageId}
              onSubmit={onSubmit}
              onSlugBlur={() =>
                checkSlugAvailability(
                  form.slug,
                )
              }
              onChange={onChange}
              onAddLink={() => openLinkModal()}
              onEditLink={link =>
                openLinkModal(link)
              }
              onDeleteLink={handleDeleteLink}
              saving={saving}
              message={message}
            />

            {canManageLinks && (
              <Card className="p-8 border-neutral-200/60 shadow-xl shadow-neutral-900/5 bg-white/80 backdrop-blur-sm">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-1">{t.pageBuilder.content.title}</h3>
                  <p className="text-sm text-neutral-500 mb-6">{t.pageBuilder.content.subtitle}</p>

                  <div className="space-y-4 mb-6 p-4 bg-neutral-50 rounded-xl border border-neutral-200">
                    <div className="flex items-center justify-between">
                      <label htmlFor="show_links_label" className="text-sm font-medium text-neutral-900 cursor-pointer select-none">
                        {t.pageBuilder.content.showLinksTitle}
                      </label>
                      <div className="relative inline-block w-10 h-6 align-middle select-none transition duration-200 ease-in">
                        <input
                          type="checkbox"
                          name="show_links_label"
                          id="show_links_label"
                          checked={form.show_links_label}
                          onChange={(e) => {
                            setForm(prev => ({ ...prev, show_links_label: e.target.checked }));
                          }}
                          className="toggle-checkbox absolute block w-4 h-4 rounded-full bg-white border-4 appearance-none cursor-pointer transition-transform duration-200 ease-in-out checked:translate-x-4"
                          style={{ top: '4px', left: '4px' }}
                        />
                        <label
                          htmlFor="show_links_label"
                          className={`toggle-label block overflow-hidden h-6 rounded-full cursor-pointer transition-colors duration-200 ${form.show_links_label ? 'bg-neutral-900' : 'bg-neutral-300'}`}
                        ></label>
                      </div>
                    </div>

                    {form.show_links_label && (
                      <div className="space-y-2">
                        <label
                          htmlFor="links_label_content"
                          className="text-sm font-medium text-neutral-700"
                        >
                          {t.pageBuilder.content.sectionTitleLabel}
                        </label>
                        <input
                          id="links_label_content"
                          value={form.links_label}
                          onChange={e =>
                            setForm(prev => ({ ...prev, links_label: e.target.value.slice(0, 120) }))
                          }
                          placeholder={t.pageBuilder.content.sectionTitlePlaceholder}
                          className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900 focus-visible:border-transparent placeholder:text-neutral-400 bg-white"
                        />
                      </div>
                    )}
                  </div>

                  <SegmentedControl
                    options={[
                      { value: "links" as const, label: t.pageBuilder.content.tabs.links },
                      { value: "social" as const, label: t.pageBuilder.content.tabs.social },
                    ]}
                    value={activeTab}
                    onChange={setActiveTab}
                  />

                  <div className="mt-6">
                    {activeTab === "links" ? (
                      <LinksList
                        links={links}
                        canManageLinks={canManageLinks}
                        onAdd={openLinkModal}
                        onEdit={openLinkModal}
                        onDelete={handleDeleteLink}
                      />
                    ) : (
                      <SocialLinks
                        pageId={pageId}
                        initialLinks={socialLinks}
                        onUpdate={setSocialLinks}
                      />
                    )}
                  </div>
                </div>
              </Card>
            )}

            {isEditing && (
              <div className="flex justify-end pt-4">
                <DeletePageTrigger
                  visible={true}
                  onClick={() => setDeleteModalOpen(true)}
                />
              </div>
            )}
          </div>

          <div className="hidden lg:block sticky top-8">
            <div className="flex flex-col items-center space-y-4">
              <div className="text-sm font-medium text-neutral-500 uppercase tracking-wider">{t.pageBuilder.preview.label}</div>
              <div className="relative w-[320px] h-[680px] bg-neutral-900 rounded-[3rem] shadow-2xl border-[8px] border-neutral-900 overflow-hidden ring-1 ring-black/5">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-neutral-900 rounded-b-2xl z-20"></div>
                <div className="w-full h-full bg-white overflow-y-auto scrollbar-hide">
                  <PageView
                    page={{
                      slug: form.slug,
                      title: form.title,
                      links_label: form.links_label,
                      show_links_label: form.show_links_label,
                      logo_base64: form.logo_base64,
                      description: form.description,
                      background_color: form.background_color,
                      links: links,
                      social_links: socialLinks,
                    }}
                    showCta={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <LinkModal
        open={linkModalState.open}
        loading={linkModalState.saving}
        error={linkModalState.error}
        linkForm={linkModalState.form}
        editingLink={linkModalState.editingLink}
        onClose={closeLinkModal}
        onSubmit={submitLinkForm}
        onChange={onLinkFormChange}
      />

      <DeletePageModal
        open={deleteModalOpen}
        deleting={deleting}
        onCancel={() => setDeleteModalOpen(false)}
        onConfirm={handleDeletePage}
      />

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
